import React from "react";
import { AddonParams, BaseAddon } from "../base/BaseAddon";
import {
  StepChangeEvent,
  StepType,
  UploaderSubmissionData,
  ValidationError,
  ValidationErrorCode,
  ValidationResult,
  ValidationWarning,
  ValidationWarningCode
} from "../../types/types";
import toastService from "../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../core/toaster/Toaster_model";
import { ValidateDataView } from "./ValidateDataAddon_view";
import { NoDataFound } from "../../components/NotFound/NoDataFound";

interface ValidateDataAddonParams<T extends string = string> extends AddonParams<T> {
  rowHook?: (row: any, addError: (fieldKey: string, error: any) => void, table: any[]) => any | Promise<any>;
  tableHook?: (
    table: any[],
    addError: (rowIndex: number, fieldKey: string, error: any) => void
  ) => any[] | Promise<any[]>;
  registeredAddons: BaseAddon[];
  onSubmit?: (data: UploaderSubmissionData<any>) => void | Promise<any>;
}

export class ValidateDataAddon<T extends string = string> extends BaseAddon<T> {
  private rowHook?: (row: any, addError: (fieldKey: string, error: any) => void, table: any[]) => any | Promise<any>;
  private tableHook?: (
    table: any[],
    addError: (rowIndex: number, fieldKey: string, error: any) => void
  ) => any[] | Promise<any[]>;
  private onBack: () => void;
  private registeredAddons: BaseAddon[] = [];
  private existingData: any[] = [];

  constructor(onNext: (event: StepChangeEvent) => void, onBack: () => void, params: ValidateDataAddonParams<T>) {
    super("ValidateDataAddon", [StepType.validateData], onNext, params);
    this.onBack = onBack;
    this.rowHook = params.rowHook;
    this.tableHook = params.tableHook;
    if (params.registeredAddons) {
      this.registeredAddons = params.registeredAddons;
    }
  }

  private loadExistingDataFromAddons = async (): Promise<any[]> => {
    let existingData: any[] = [];

    for (const addon of this.registeredAddons) {
      if (addon.getExistingData) {
        try {
          // Use addon name as key
          const data = await addon.getExistingData();
          if (data && data.length > 0) {
            existingData = [...data];
          }
        } catch (error) {
          console.error(`Error loading data from ${addon.name}:`, error);
        }
      }
    }

    this.existingData = existingData;
    return existingData;
  };

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    if (!state.data) {
      return <NoDataFound onBack={this.onBack}></NoDataFound>;
    }

    return (
      <ValidateDataView
        data={state.data || []}
        columns={state.columns || {}}
        headerValues={state.headerValues || []}
        fields={this.fields}
        entityRelations={state.entityRelations || {}}
        onValidate={this.handleValidate}
        onCellEdit={this.handleCellEdit}
        onRowDelete={this.handleRowDelete}
        onAddRow={this.handleAddRow}
        onSubmit={this.handleSubmit}
        onBack={this.onBack}
        rowHook={this.rowHook}
        tableHook={this.tableHook}
        onLoadExistingData={this.loadExistingDataFromAddons}
        existingData={this.existingData}
      />
    );
  }

  private handleValidate = async (data: any[]): Promise<ValidationResult> => {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    data.forEach(row => {
      this.fields.forEach(field => {
        if (field.required && (!row[field.key] || row[field.key] === "")) {
          errors.push({
            rowId: row.id,
            field: field.key,
            message: `${field.name} is required`,
            code: ValidationErrorCode.REQUIRED_FIELD
          });
        }
      });
    });

    const primaryKeyField = this.fields.find(f => f.required)?.key;
    const seen = new Map<string, number[]>();

    data.forEach(row => {
      const keyValue = row[primaryKeyField];
      if (keyValue) {
        if (!seen.has(keyValue)) {
          seen.set(keyValue, []);
        }
        seen.get(keyValue)!.push(row.id);
      }
    });

    seen.forEach((rowIds, keyValue) => {
      if (rowIds.length > 1) {
        const duplicateIds = rowIds.slice(1);

        duplicateIds.forEach(rowId => {
          warnings.push({
            rowId,
            field: primaryKeyField,
            message: `Duplicate ${primaryKeyField}: "${keyValue}" - original in row ${rowIds[0]}`,
            code: ValidationWarningCode.DUPLICATE_VALUE
          });
        });
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  };

  private handleCellEdit = async (rowIndex: number, fieldKey: string, newValue: any): Promise<void> => {
    // TODO: Implement cell edit logic
    // should update the data and potentially re-validate
    // console.log("Cell edit:", { rowIndex, fieldKey, newValue });
  };

  private handleRowDelete = async (rowIndex: number): Promise<void> => {
    // console.log("Row deleted at index:", rowIndex);
  };

  private handleAddRow = async (): Promise<void> => {
    // TODO: Implement add row logic
    // console.log("Add new row");
  };

  private handleSubmit = async (submissionData: UploaderSubmissionData<T>): Promise<void> => {
    try {
      const validationResult = await this.handleValidate(submissionData.rows);

      if (!validationResult.valid) {
        toastService
          .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
          .setContent(<span>Please fix validation errors before submitting</span>);
        return;
      }

      if (this.onSubmit) {
        await this.onSubmit(submissionData);
      }

      this.onNext({
        type: StepType.validateData,
        data: submissionData.rows
      });
    } catch (error) {
      console.error("Error submitting data:", error);
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{error?.message || "Error submitting data"}</span>);
    }
  };
}
